/**
 * What the customers feature contributes to the route tree.
 *
 * Each route declares the permission it needs, and the same declaration drives
 * both the router's gate and the navigation entry — so a menu item cannot
 * appear for a page the user would be refused.
 *
 * `loadTariff` is injected rather than imported. The tariff lines belong to
 * the referential, a feature may not import a sibling feature, and a tariff
 * table is not design-system material — so the application, which is the
 * composition root and may reach both, supplies it. With nothing supplied the
 * tab says so rather than rendering an empty table that would read as "this
 * customer has no conditions".
 */

import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { ReactNode } from "react";
import type { FeatureRoutes } from "@hardauto/core-router";

import { ClientScreen } from "./screens/ClientScreen";
import { ClientsScreen } from "./screens/ClientsScreen";
import { PortfolioScreen } from "./screens/PortfolioScreen";
import { SalesRepsScreen } from "./screens/SalesRepsScreen";
import { CLIENTS_PATHS } from "./paths";
import type { LoadTariff } from "./tariff";
import "./i18n";

export interface ClientsRoutesOptions {
  /** Supplied by the application: the tariff lines belong to the referential. */
  loadTariff?: LoadTariff;
  /**
   * Supplied by the application: §6.12's internal notes belong to `support`.
   *
   * Absent when that module is not installed, and the customer file's notes
   * tab then does not exist at all — a tab that renders nothing is worse than
   * a missing one.
   */
  notesPanel?: (clientId: string) => ReactNode;
}

export function clientsRoutes(
  client: ApiClient,
  options: ClientsRoutesOptions = {},
): FeatureRoutes {
  function Clients() {
    return <ClientsScreen client={client} />;
  }

  function Client() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route types — see the note in
    // `core-router/create-router.tsx`.
    const params: { id?: string } = useParams({ strict: false });
    const search: { tab?: string } = useSearch({ strict: false });
    const navigate = useNavigate();

    return (
      <ClientScreen
        client={client}
        id={params.id ?? ""}
        {...(search.tab ? { tab: search.tab } : {})}
        {...(options.loadTariff ? { loadTariff: options.loadTariff } : {})}
        {...(options.notesPanel ? { notesPanel: options.notesPanel } : {})}
        onTabChange={(tab) => {
          // The tab lives in the URL: a link to "this customer's tariff
          // conditions" has to be a link, and back has to undo a tab change.
          // `replace`, so walking the tabs does not fill the history.
          void navigate({ to: ".", search: { tab }, replace: true });
        }}
      />
    );
  }

  function SalesReps() {
    return <SalesRepsScreen client={client} />;
  }

  function Portfolio() {
    return <PortfolioScreen client={client} />;
  }

  return {
    id: "clients",
    routes: [
      {
        path: CLIENTS_PATHS.clients,
        component: Clients,
        layout: "app",
        requirement: { authenticated: true, permissions: ["clients.client.read"] },
        nav: {
          group: "clients:group",
          labelKey: "clients:nav.clients",
          section: "main",
          order: 10,
        },
      },
      {
        path: CLIENTS_PATHS.client,
        component: Client,
        layout: "app",
        requirement: { authenticated: true, permissions: ["clients.client.read"] },
      },
      {
        path: CLIENTS_PATHS.salesReps,
        component: SalesReps,
        layout: "app",
        requirement: { authenticated: true, permissions: ["clients.rep.read"] },
        nav: {
          group: "clients:group",
          labelKey: "clients:nav.salesReps",
          section: "main",
          order: 11,
        },
      },
      {
        path: CLIENTS_PATHS.portfolio,
        component: Portfolio,
        layout: "app",
        // Its own permission, not `clients.client.read`: a representative sees
        // their own customers without being able to page the whole base.
        requirement: { authenticated: true, permissions: ["clients.portfolio.read"] },
        nav: {
          group: "clients:group",
          labelKey: "clients:nav.portfolio",
          section: "main",
          order: 12,
        },
      },
    ],
  };
}
